/**
 * Hero Section Video Lazy Loading Module
 * Enhanced with mobile video support and responsive handling
 */

const createAndPlayVideo = (container, videoUrl, deviceType = 'desktop') => {
  // Check if video already exists to prevent duplicates
  if (container.querySelector('video')) {
    return;
  }

  // Create the video element with required attributes
  const video = document.createElement('video');
  video.className = `hero__video hero__video--${deviceType}`;
  video.autoplay = true;
  video.muted = true;
  video.loop = true;
  video.playsInline = true;
  
  // Create and configure the source element
  const source = document.createElement('source');
  source.src = videoUrl;
  source.type = 'video/mp4';
  
  // Append source to video element
  video.appendChild(source);
  
  // Insert video into the container
  container.appendChild(video);
  
  // Trigger video loading
  video.load();
  
  const playVideo = async () => {
    try {
      // Attempt to play the video
      await video.play();
      console.log(`${deviceType} video playing successfully`);
      
      // Find and fade out the corresponding placeholder
      const heroSection = container.closest('.hero');
      if (heroSection) {
        const placeholder = heroSection.querySelector(`.hero__placeholder--${deviceType}`);
        if (placeholder) {
          // Set opacity to 0 for fade effect
          placeholder.style.opacity = '0';
          // Remove placeholder from DOM after transition
          setTimeout(() => {
            placeholder.style.display = 'none';
          }, 500);
        }
      }
    } catch (error) {
      // Log any playback errors
      console.error(`${deviceType} video play failed:`, error);
    }
  };
  
  // Attach event listeners for when video is ready
  video.addEventListener('loadeddata', playVideo, { once: true });
  video.addEventListener('canplay', playVideo, { once: true });
  
  // Fallback to ensure video plays after 1 second
  setTimeout(playVideo, 1000);
};

const shouldLoadVideo = (deviceType) => {
  const isMobile = window.innerWidth <= 768;
  
  if (deviceType === 'mobile' && isMobile) return true;
  if (deviceType === 'desktop' && !isMobile) return true;
  
  return false;
};

const initLazyVideos = () => {
  // Select all containers marked for lazy video loading
  const containers = document.querySelectorAll('[data-video-container]');
  
  // Exit early if no containers found
  if (containers.length === 0) {
    console.log('No video containers found');
    return;
  }
  
  // Process each container
  containers.forEach(container => {
    // Extract video URL and device type from data attributes
    const videoUrl = container.dataset.videoUrl;
    const deviceType = container.dataset.videoContainer || 'desktop';
    
    // Skip if no URL provided
    if (!videoUrl) {
      console.error('No video URL found for lazy loading');
      return;
    }

    // Only load video if it's appropriate for current viewport
    if (!shouldLoadVideo(deviceType)) {
      console.log(`Skipping ${deviceType} video for current viewport`);
      return;
    }
    
    // Check if we should lazy load based on data attribute
    const shouldLazyLoad = container.dataset.lazyLoad === 'true';
    
    if (shouldLazyLoad) {
      // Use IntersectionObserver for modern browsers
      if ('IntersectionObserver' in window) {
        // Create observer with custom options
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            // Load video when container enters viewport
            if (entry.isIntersecting) {
              createAndPlayVideo(container, videoUrl, deviceType);
              // Stop observing after loading
              observer.unobserve(container);
            }
          });
        }, {
          // Start loading 100px before element enters viewport
          rootMargin: '100px',
          // Trigger as soon as any part is visible
          threshold: 0
        });
        
        // Start observing the container
        observer.observe(container);
      } else {
        // Fallback for browsers without IntersectionObserver
        createAndPlayVideo(container, videoUrl, deviceType);
      }
    } else {
      // Load immediately if lazy loading is disabled
      createAndPlayVideo(container, videoUrl, deviceType);
    }
  });
};

const handleResponsiveVideoSwitch = () => {
  const containers = document.querySelectorAll('[data-video-container]');
  
  containers.forEach(container => {
    const deviceType = container.dataset.videoContainer || 'desktop';
    const videoUrl = container.dataset.videoUrl;
    const video = container.querySelector('video');
    
    if (shouldLoadVideo(deviceType)) {
      // Load video if it should be visible but doesn't exist
      if (!video && videoUrl) {
        const shouldLazyLoad = container.dataset.lazyLoad === 'true';
        if (!shouldLazyLoad) {
          createAndPlayVideo(container, videoUrl, deviceType);
        }
      }
    } else {
      // Pause video if it shouldn't be visible
      if (video) {
        video.pause();
      }
    }
  });
  
  // Show appropriate placeholders
  showCorrectPlaceholders();
};

const showCorrectPlaceholders = () => {
  const placeholders = document.querySelectorAll('.hero__placeholder--desktop, .hero__placeholder--mobile');
  const isMobile = window.innerWidth <= 768;
  
  placeholders.forEach(placeholder => {
    const isDesktopPlaceholder = placeholder.classList.contains('hero__placeholder--desktop');
    const isMobilePlaceholder = placeholder.classList.contains('hero__placeholder--mobile');
    
    if ((isDesktopPlaceholder && !isMobile) || (isMobilePlaceholder && isMobile)) {
      // Check if corresponding video is loaded
      const heroSection = placeholder.closest('.hero');
      const deviceType = isDesktopPlaceholder ? 'desktop' : 'mobile';
      const videoContainer = heroSection ? heroSection.querySelector(`.hero__video-container--${deviceType}`) : null;
      const video = videoContainer ? videoContainer.querySelector('video') : null;
      
      if (!video || video.readyState < 2) {
        placeholder.style.display = 'block';
        placeholder.style.opacity = '1';
      }
    } else {
      placeholder.style.display = 'none';
    }
  });
};

// Handle responsive video switching on resize with debouncing
let resizeTimer;
const handleResize = () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    handleResponsiveVideoSwitch();
  }, 250);
};

// Initialize based on document ready state
if (document.readyState === 'loading') {
  // Wait for DOM to be ready if still loading
  document.addEventListener('DOMContentLoaded', () => {
    initLazyVideos();
    window.addEventListener('resize', handleResize);
  });
} else {
  // Initialize immediately if DOM is already ready
  initLazyVideos();
  window.addEventListener('resize', handleResize);
}

// Utility function for debugging
const debugHeroVideoState = () => {
  const heroSections = document.querySelectorAll('.hero--video');
  
  heroSections.forEach((hero, index) => {
    console.log(`Hero Section ${index + 1}:`);
    
    const desktopVideo = hero.querySelector('.hero__video-container--desktop video');
    const mobileVideo = hero.querySelector('.hero__video-container--mobile video');
    const desktopPlaceholder = hero.querySelector('.hero__placeholder--desktop');
    const mobilePlaceholder = hero.querySelector('.hero__placeholder--mobile');
    
    console.log('Desktop video:', desktopVideo ? 'Loaded' : 'Not loaded');
    console.log('Mobile video:', mobileVideo ? 'Loaded' : 'Not loaded');
    console.log('Desktop placeholder visible:', desktopPlaceholder ? getComputedStyle(desktopPlaceholder).display !== 'none' : 'N/A');
    console.log('Mobile placeholder visible:', mobilePlaceholder ? getComputedStyle(mobilePlaceholder).display !== 'none' : 'N/A');
    console.log('Current viewport:', window.innerWidth <= 768 ? 'Mobile' : 'Desktop');
    console.log('---');
  });
};

// Expose debug function to window for manual testing
window.debugHeroVideoState = debugHeroVideoState;